import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { History, Clock, Download, Eye, ArrowRight, Calendar, Image, FileAudio } from 'lucide-react'

export function HistoryPagePreview() {
  useEffect(() => {
    // SEO优化：设置页面特定的meta标签
    document.title = '创作历史 - 查看您的AI生成内容 | KeyCore AI Studio'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', '查看和管理您的AI生成历史。保存所有生成的图像和语音内容，支持下载、分享和重新编辑。')
    }

    // Open Graph标签
    const setMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta') as HTMLMetaElement
        meta.setAttribute('property', property)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    setMetaTag('og:title', '创作历史 - 查看您的AI生成内容')
    setMetaTag('og:description', '管理您的AI创作历史，保存和下载所有生成的内容，包括图像和语音。')
    setMetaTag('og:image', 'https://ai-studio.keycore.tech/og-history.jpg')
    setMetaTag('og:url', 'https://ai-studio.keycore.tech/history')
    setMetaTag('og:type', 'website')
  }, [])

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* SEO优化的结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "创作历史管理",
          "description": "查看和管理您的AI生成内容历史，支持图像和语音内容的保存和下载",
          "url": "https://ai-studio.keycore.tech/history",
          "applicationCategory": "DeveloperApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "featureList": [
            "创作历史查看",
            "内容下载",
            "项目管理",
            "快速访问"
          ],
          "author": {
            "@type": "Organization",
            "name": "KeyCore AI Studio"
          }
        })}
      </script>

      {/* Header */}
      <header className="relative z-10 px-4 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-white hover:text-cyan-300 transition-colors">
            <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
              <History className="w-5 h-5" />
            </div>
            <span className="font-bold text-xl">KeyCore AI</span>
          </Link>
          <Link
            to="/auth"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            开始创作
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 px-4 py-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Clock className="w-4 h-4" />
            <span>创作历史管理</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            创作历史
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              永久保存
            </span>
          </h1>

          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            您的所有AI创作作品都安全保存在云端。随时查看、下载和管理您的图像和语音生成历史。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link
              to="/auth"
              className="group inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-2xl"
            >
              <History className="w-6 h-6" />
              <span>查看我的历史</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-cyan-900/20 hover:bg-cyan-900/30 border border-cyan-900/30 rounded-xl font-semibold text-lg transition-all duration-200 backdrop-blur-sm"
            >
              <span>了解更多</span>
            </Link>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-lg mb-4">
                <Download className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">一键下载</h3>
              <p className="text-gray-400">所有生成的内容都可以免费下载到本地</p>
            </div>

            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-lg mb-4">
                <Calendar className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">时间线查看</h3>
              <p className="text-gray-400">按时间顺序浏览您的创作历程</p>
            </div>

            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-lg mb-4">
                <Eye className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">快速预览</h3>
              <p className="text-gray-400">悬停查看缩略图，快速找到想要的内容</p>
            </div>
          </div>

          {/* Content Types */}
          <div className="bg-[#161b22] rounded-2xl p-8 border border-cyan-900/30 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8">支持的内容类型</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/20">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <Image className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">AI图像</h3>
                    <p className="text-gray-400">所有生成的图像作品</p>
                  </div>
                </div>
                <ul className="text-gray-300 space-y-2">
                  <li>• 高分辨率图像下载</li>
                  <li>• 生成参数记录</li>
                  <li>• 创作时间戳</li>
                  <li>• 描述文本保存</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/20">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FileAudio className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">语音内容</h3>
                    <p className="text-gray-400">所有合成的语音文件</p>
                  </div>
                </div>
                <ul className="text-gray-300 space-y-2">
                  <li>• 高质量音频下载</li>
                  <li>• 语音参数记录</li>
                  <li>• 文本内容保存</li>
                  <li>• 时长信息显示</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-[#161b22] rounded-2xl p-8 border border-cyan-900/30 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8">历史管理功能</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">自动保存</h3>
                <p className="text-gray-400">每次生成的内容都会自动保存到历史记录中</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">智能搜索</h3>
                <p className="text-gray-400">通过关键词、日期等快速找到特定内容</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">批量操作</h3>
                <p className="text-gray-400">支持批量下载和管理多个创作项目</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/auth"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-12 py-5 rounded-2xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <History className="w-7 h-7" />
              <span>开始创作之旅</span>
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
